import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import type { Option } from '@/constants/institute';
import type { InstituteClass } from '@/types/instituteClass';

export interface DuplicateClassModalProps {
  /** The class being copied. Null keeps the dialog closed. */
  source: InstituteClass | null;
  /** Every class name on the list — see `classNameOptions`. */
  nameOptions: Option[];
  isLoading: boolean;
  onConfirm: (name: string) => void;
  onCancel: () => void;
}

/**
 * Copying a class under a name of its own.
 *
 * A copy is a new class, and every class takes its name from the list — so the
 * office picks which class this becomes rather than getting "Class 8 (copy)"
 * to rename afterwards. A name the institute already uses is refused by the
 * server, and that message is shown on the page.
 */
export function DuplicateClassModal({
  source,
  nameOptions,
  isLoading,
  onConfirm,
  onCancel,
}: DuplicateClassModalProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  // A fresh choice each time it opens, so the last copy's name is never reused.
  useEffect(() => {
    setName('');
    setError(null);
  }, [source]);

  function confirm() {
    if (!name) {
      setError('Choose a name for the new class');
      return;
    }
    onConfirm(name);
  }

  return (
    <Modal
      isOpen={Boolean(source)}
      onClose={onCancel}
      size="sm"
      title={`Duplicate ${source?.name ?? 'this class'}`}
      description="A new class is created with the same sections, subjects and fees. No students are copied."
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={confirm} isLoading={isLoading}>
            Duplicate class
          </Button>
        </>
      }
    >
      <Select
        label="Name of the new class"
        placeholder="Choose a class"
        options={nameOptions}
        value={name}
        error={error ?? undefined}
        onChange={(event) => {
          setName(event.target.value);
          setError(null);
        }}
      />
    </Modal>
  );
}
